/**
 * The person's identity in a URL: the `/in/<handle>` part of their canonical
 * LinkedIn URL (epic 2026-09-21, "Shareable URLs").
 *
 * ONE helper, because the handle is read from three places that must agree —
 * a contacts-table row (`linkedin_url`, as captured), the in-list `?person=`
 * param and the standalone `/people/in/:handle` route — and the API resolves it
 * with `normalize_linkedin_url` (`nu_assist_shared/identity.py`). This mirrors
 * that canonicalizer's reading of a URL: scheme, `www.`/`m.`, query, fragment
 * and trailing slash are dropped, the host is case-folded, the handle keeps its
 * case (03 matches it case-sensitively).
 */

export type PersonTab = "profile" | "activity" | "sync" | "ai-review" | "company-map";

export const PERSON_TABS: readonly PersonTab[] = [
  "profile",
  "activity",
  "sync",
  "ai-review",
  "company-map",
];

/** The tabs grouped under the panel's "AI Prompts" tab. They stay URL values of
 *  their own (`?tab=ai-review`, `?tab=company-map`), so links keep working; the
 *  grouping is only how the panel draws them. */
export const AI_PROMPT_TABS = ["ai-review", "company-map"] as const satisfies readonly PersonTab[];

export function isAiPromptTab(tab: PersonTab): tab is (typeof AI_PROMPT_TABS)[number] {
  return (AI_PROMPT_TABS as readonly PersonTab[]).includes(tab);
}

/** Same character class the API's `{handle}` path parameter accepts
 *  (`^[\w\-.%~]{1,200}$`); anything else would be a 422, so it is not a handle.
 *  The route validates that pattern with pydantic-core's (Rust) regex engine,
 *  whose Unicode `\w` admits combining marks (`\p{M}`); Python's `re` `\w`
 *  does NOT, which is why the server normalizes to NFC before its own match
 *  (`nu_assist_shared/identity.py`). A decomposed (NFD) accent therefore passes
 *  the route and must pass here too. */
const HANDLE_PATTERN = /^[\p{L}\p{M}\p{N}_\-.%~]{1,200}$/u;

/** `https://www.linkedin.com/in/Jane-Doe/?utm=x` → `Jane-Doe`; null when the
 *  URL carries no LinkedIn profile path. */
export function linkedinHandle(url: string | null | undefined): string | null {
  if (typeof url !== "string") return null;
  const raw = url.trim();
  if (!raw) return null;
  const noScheme =
    raw
      .replace(/^[a-z]+:\/\//i, "")
      .split("?")[0]
      ?.split("#")[0] ?? "";
  const slash = noScheme.indexOf("/");
  if (slash < 0) return null;
  const host = noScheme
    .slice(0, slash)
    .toLowerCase()
    .replace(/^(www\.|m\.)/, "");
  if (!host.includes("linkedin.com")) return null;
  const segments = noScheme
    .slice(slash + 1)
    .split("/")
    .filter(Boolean);
  if (segments.length < 2 || segments[0]?.toLowerCase() !== "in") return null;
  const handle = segments[1] ?? "";
  return isPersonHandle(handle) ? handle : null;
}

/** Whether a URL-supplied value (`?person=`, `:handle`) can be a handle. */
export function isPersonHandle(value: string | null | undefined): value is string {
  return typeof value === "string" && HANDLE_PATTERN.test(value);
}

/** `?tab=` → a known tab; anything else (absent, typo, old link) is Profile. */
export function parsePersonTab(value: string | null | undefined): PersonTab {
  return PERSON_TABS.includes(value as PersonTab) ? (value as PersonTab) : "profile";
}

/** In-list panel URL: `/research/imports/:importId?…&person=<handle>&tab=<tab>`.
 *  `params` are the page's current search params, kept so a shared link lands
 *  on the same view. */
export function personInListPath(
  importId: string,
  handle: string,
  tab: PersonTab,
  params: URLSearchParams = new URLSearchParams()
): string {
  const next = new URLSearchParams(params);
  next.set("person", handle);
  next.set("tab", tab);
  return `/research/imports/${encodeURIComponent(importId)}?${next.toString()}`;
}
